import { useContext } from "react"
import i18n from "i18n"
import { API, Auth } from "aws-amplify"
import TemplatesManagerContext from "context/templates/TemplatesManagerContext"
import { useSnackbar } from "notistack"
import {
  DELETE_TEMPLATE,
  SET_DELETE_TEMPLATE,
  SET_EDIT_TEMPLATE,
  SET_LOADING,
  SET_TAB,
  SET_TEMPLATES,
} from "context/types"
const CORE = "CORE"

const useTemplatesManager = () => {
  const context = useContext(TemplatesManagerContext)
  if (!context) {
    throw new Error(
      "useTemplatesManager must be used within a TemplatesManagerState provider",
    )
  }

  const { state, dispatch } = context
  const { enqueueSnackbar } = useSnackbar()

  const getHeaders = async () => {
    return {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
      "Access-Control-Allow-Origin": "*",
    }
  }

  const createTemplate = async newTemplate => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const path = "/templates"
      await API.post(CORE, path, {
        headers,
        body: newTemplate,
      })

      enqueueSnackbar(i18n.t("snackbar.success.create"), {
        variant: "success",
      })
    } catch (error) {
      console.error("Error creating template:", error)
      enqueueSnackbar(i18n.t("snackbar.error.default"), {
        variant: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const fetchTemplates = async (organizationId, showDefault = true) => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const path = `/templates/${organizationId}?showDefault=${showDefault}`
      const res = await API.get(CORE, path, { headers })
      dispatch({ type: SET_TEMPLATES, payload: res })
    } catch (error) {
      console.error("Error fetching templates:", error)
      enqueueSnackbar(i18n.t("snackbar.error.default"), {
        variant: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const deleteTemplate = async (templateId, organizationId) => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const path = `/templates/${templateId}`
      await API.del(CORE, path, { headers })

      const templatesUpdated = state.templates.filter(
        template =>
          !(
            template.projectId === templateId &&
            template.organizationId === organizationId
          ),
      )

      dispatch({
        type: DELETE_TEMPLATE,
        payload: {
          templates: templatesUpdated,
        },
      })

      confirmDelete(false, null, null)

      enqueueSnackbar(i18n.t("snackbar.success.delete"), {
        variant: "success",
      })
    } catch (error) {
      console.error("Error deleting template:", error)
      enqueueSnackbar(i18n.t("snackbar.error.deleteElement"), {
        variant: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const duplicateToTemplate = async (templateId, organizationId) => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const path = `/templates/copy_to_template`

      await API.post(CORE, path, {
        headers,
        body: {
          organizationId: organizationId,
          projectId: templateId,
          is_template: true,
        },
      })

      enqueueSnackbar(i18n.t("snackbar.success.default"), {
        variant: "success",
      })

      await fetchTemplates(organizationId)
    } catch (error) {
      console.error("Error duplicating template:", error)
      enqueueSnackbar(i18n.t("snackbar.error.default"), {
        variant: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const duplicateFromTemplate = async (templateId, organizationId) => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const path = `/templates/copy_from_template`

      await API.post(CORE, path, {
        headers,
        body: {
          organizationId: organizationId,
          projectId: templateId,
          is_template: false,
        },
      })

      enqueueSnackbar(i18n.t("snackbar.success.default"), {
        variant: "success",
      })
    } catch (error) {
      console.error("Error duplicating template:", error)
      enqueueSnackbar(i18n.t("snackbar.error.default"), {
        variant: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const updateTemplateName = async (name, projectId, organizationId) => {
    setLoading(true)
    try {
      const apiName = "dev-PLANHOPPER-API"
      const path = "/api/project/" + projectId
      const headers = await getHeaders()

      const res = await API.get(apiName, path, { headers })

      const pathToUpdate = "/api/project"

      const body = {
        ...res.project,
        name,
      }

      await API.put(apiName, pathToUpdate, { headers, body })

      await fetchTemplates(organizationId)

      enqueueSnackbar(i18n.t("snackbar.success.update"), {
        variant: "success",
      })
    } catch (error) {
      console.log("🚀 ~ error:", error)
      enqueueSnackbar(i18n.t("snackbar.error.default"), {
        variant: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const openEditTemplate = (open, projectId, organizationId) => {
    try {
      setLoading(true)

      const template = state.templates.find(
        template =>
          template.projectId === projectId &&
          template.organizationId === organizationId,
      )
      dispatch({
        type: SET_EDIT_TEMPLATE,
        payload: { open, template: template },
      })
    } catch (error) {
      console.log("🚀 ~ error:", error)
      enqueueSnackbar(i18n.t("snackbar.error.default"), {
        variant: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const closeEditTemplate = () => {
    dispatch({
      type: SET_EDIT_TEMPLATE,
      payload: { open: false, template: null },
    })
  }

  const confirmDelete = (open, projectId, organizationId) => {
    dispatch({
      type: SET_DELETE_TEMPLATE,
      payload: { open, template: { projectId, organizationId } },
    })
  }

  const setLoading = isLoading => {
    dispatch({ type: SET_LOADING, payload: isLoading })
  }

  const setTabSelected = tab => {
    dispatch({ type: SET_TAB, payload: tab })
  }

  return {
    state,
    createTemplate,
    fetchTemplates,
    deleteTemplate,
    confirmDelete,
    duplicateToTemplate,
    duplicateFromTemplate,
    setLoading,
    openEditTemplate,
    closeEditTemplate,
    updateTemplateName,
    setTabSelected,
  }
}

export default useTemplatesManager
